import React from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #07090e;
  --panel: #0d111a;
  --surface: #131824;
  --text: #f3f4f8;
  --dim: #9aa0b2;
  --faint: #636878;
  --accent: #38bdf8;
  --accent-glow: rgba(56, 189, 248, 0.15);
  --success: #34d399;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.68;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 780px;
  margin: 0 auto;
  padding: 44px 22px 90px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 24px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(145deg, #0284c7, #38bdf8);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: 24px;
  box-shadow: 0 8px 24px rgba(14, 165, 233, 0.35);
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -1px;
  margin: 8px 0 6px;
  color: #ffffff;
`;

const Updated = styled.p`
  color: var(--faint);
  font-size: 14px;
  margin-bottom: 32px;
`;

const Section = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 38px 0 12px;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
    flex: none;
  }
`;

const Body = styled.div`
  p,
  li {
    color: var(--dim);
    font-size: 15.5px;
    line-height: 1.7;
    margin-bottom: 12px;
  }

  ul {
    padding-left: 20px;
    margin-bottom: 18px;
  }

  li {
    margin-bottom: 6px;
  }

  strong {
    color: var(--text);
  }

  a {
    color: var(--accent);
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Card = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 22px 24px;
  margin: 18px 0 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
`;

const HighlightBox = styled(Card)`
  background: linear-gradient(145deg, rgba(14, 165, 233, 0.08), rgba(99, 102, 241, 0.05));
  border: 1px solid rgba(56, 189, 248, 0.25);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin: 18px 0;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureBox = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px 18px;

  h4 {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: var(--dim);
  }
`;

const Footer = styled.footer`
  margin-top: 60px;
  padding-top: 24px;
  border-top: 1px solid var(--border);
  color: var(--faint);
  font-size: 13.5px;
  text-align: center;
`;

const CalcMaster = () => {
  return (
    <Page>
      <Wrap>
        <Header>
          <Badge>∑</Badge>
          <div>
            <Brand>
              NoteCalc<span>Pro</span>
            </Brand>
            <span style={{ fontSize: "13px", color: "var(--faint)" }}>
              Smart Notepad &amp; Precision Calculator
            </span>
          </div>
        </Header>

        <Title>Privacy Policy</Title>
        <Updated>Last updated: September 8, 2026</Updated>

        <Body>
          <HighlightBox>
            <p style={{ margin: 0, color: "#e0f2fe", fontSize: "16px" }}>
              <strong>Summary &amp; Guiding Principle:</strong> NoteCalc Pro (com.zaynmiraj.calmaster) is designed with an
              uncompromising <strong>offline-first, privacy-by-design</strong> architecture. Your
              calculation notes, formulas, equations, history, and personal financial figures remain
              entirely on your device. We do not track, collect, sell, or monitor your calculations.
            </p>
          </HighlightBox>

          <Section>1. Core Architecture: 100% On-Device</Section>
          <p>
            NoteCalc Pro operates as an on-device calculation suite and reactive smart notepad. When
            you write equations, link lines (e.g. <code>Line 1 - Line 2</code>), declare variables,
            or compute financial figures, all mathematical computations are evaluated locally via
            our client-side calculation engine.
          </p>
          <ul>
            <li>
              <strong>No Cloud Storage of Calculations:</strong> Your notes and ledger tapes are
              saved exclusively to your device&apos;s sandboxed local storage (<code>AsyncStorage</code>).
            </li>
            <li>
              <strong>No Account Required:</strong> You do not need to create an account, log in, or
              provide an email address or phone number to use NoteCalc Pro.
            </li>
            <li>
              <strong>Offline Capable:</strong> The entire calculator, memory registers, unit logic,
              and Smart Notepad function seamlessly without an active internet connection.
            </li>
          </ul>

          <Section>2. Permissions &amp; Data Access</Section>
          <p>
            NoteCalc Pro requests only minimal, optional permissions that are strictly necessary to
            support specific user-triggered features:
          </p>

          <Grid>
            <FeatureBox>
              <h4>🎙️ Microphone (Optional)</h4>
              <p>
                Used solely when you tap the microphone button for Voice Math Dictation. Audio is
                streamed ephemerally to parse numbers and formulas. Voice recordings are never stored,
                logged, or used for voice profiling.
              </p>
            </FeatureBox>

            <FeatureBox>
              <h4>📋 Clipboard (Optional)</h4>
              <p>
                Accessed only when you explicitly press &ldquo;Paste&rdquo; to insert numbers or
                equations, or tap &ldquo;Copy&rdquo; to copy results. The app never accesses the
                clipboard in the background.
              </p>
            </FeatureBox>
          </Grid>

          <Section>3. Network Usage &amp; Third-Party Services</Section>
          <p>
            NoteCalc Pro connects to the internet strictly for two transparent, user-facing capabilities:
          </p>
          <ul>
            <li>
              <strong>Real-Time Currency Exchange Rates:</strong> To enable currency conversions (e.g.{" "}
              <code>420 EUR to USD</code>), the app periodically checks public exchange rates over HTTPS.
              No personal information, device identifiers, or user calculation data are sent during
              these rate checks.
            </li>
            <li>
              <strong>AI Financial Intelligence (Optional User Action):</strong> When you explicitly
              request an AI Executive Summary or Financial Audit of your active note, the text lines
              of that note are processed solely to generate the requested analysis. No identifying
              metadata, accounts, or profiling information are attached.
            </li>
          </ul>

          <Section>4. Advertising &amp; Third-Party Services</Section>
          <p>
            NoteCalc Pro utilizes <strong>Google AdMob</strong> (Google Mobile Ads SDK) to display
            advertisements (such as banner ads, transition interstitials, and user-initiated rewarded ads)
            which support free ongoing development of the app.
          </p>
          <ul>
            <li>
              <strong>AdMob Data Collection:</strong> Google AdMob may automatically collect pseudonymous
              technical data such as device identifiers (e.g. Android Advertising ID / AAID), coarse IP-based
              location, and app interaction telemetry strictly for fraud prevention, ad delivery, and diagnostic
              reporting.
            </li>
            <li>
              <strong>Strict Financial Isolation:</strong> Your calculations, notes, formulas, equations,
              and ledger tapes are processed exclusively on your device and are <strong>never</strong> shared
              with, transmitted to, or accessible by Google AdMob or any advertisers.
            </li>
            <li>
              <strong>Google Privacy Policy:</strong> For more details on how Google processes ad data, please
              review <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>Google&apos;s Advertising Privacy &amp; Terms</a>.
            </li>
          </ul>

          <Section>5. Data Retention, Export &amp; Deletion</Section>
          <p>
            Because all notes, calculation histories, and preferences live on your device, you maintain
            full sovereignty over your data:
          </p>
          <ul>
            <li>
              <strong>In-App Deletion:</strong> You can delete any note, clear calculation lines, or
              purge the entire calculation history tape at any time from the app settings.
            </li>
            <li>
              <strong>Full Data Export:</strong> You can export a full JSON backup of your notes and
              ledger histories directly from the Settings screen.
            </li>
            <li>
              <strong>Complete Device Removal:</strong> Uninstalling NoteCalc Pro or clearing the
              app&apos;s storage in your operating system settings permanently removes all locally
              persisted data.
            </li>
          </ul>

          <Section>6. Children&apos;s Privacy (COPPA &amp; GDPR Compliance)</Section>
          <p>
            NoteCalc Pro is suitable for general audiences and educational use. We do not knowingly
            collect, request, or solicit personal information from children under the age of 13.
            Because no personal information is gathered from any user, the application is fully
            compliant with COPPA and global child privacy regulations.
          </p>

          <Section>7. Security</Section>
          <p>
            Any network communications (such as currency rate updates) use industry-standard Transport
            Layer Security (TLS/HTTPS). All local notes and calculations remain isolated within the
            app&apos;s secure, sandboxed OS directory.
          </p>

          <Section>8. Changes to This Policy</Section>
          <p>
            We may periodically update our Privacy Policy to reflect app enhancements or legal
            requirements. Any modifications will be posted to this page with an updated revision date.
          </p>

          <Section>9. Contact Us</Section>
          <Card>
            If you have questions regarding this Privacy Policy or NoteCalc Pro&apos;s privacy practices,
            please reach out to us:
            <br />
            <br />
            <strong>Developer / Support:</strong> Nimions / Zayn Miraj
            <br />
            <strong>Email:</strong>{" "}
            <a href="mailto:hello@nimions.com">hello@nimions.com</a>
            <br />
            <strong>Data Deletion Requests:</strong>{" "}
            <a href="/notecalc-pro/delete-account">/notecalc-pro/delete-account</a>
          </Card>
        </Body>

        <Footer>
          &copy; 2026 NoteCalc Pro. This privacy policy applies to the NoteCalc Pro mobile application
          (Package <strong>com.zaynmiraj.calmaster</strong>).
        </Footer>
      </Wrap>
    </Page>
  );
};

export default CalcMaster;
